<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-sm-flex align-items-center">
        <a class="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#AddConfigurationModal"
          @click="resetModal">
          <i class="fa fa-plus-circle me-1"></i>
          Ajouter une configuration
        </a>
      </div>
      <div class="d-flex align-items-center">
        <form class="search-bg svg-color pt-3" @submit.prevent="rechercher">
          <input type="text" v-model="searchTerm" @keyup="rechercher" class="form-control shadow-none text-black"
            placeholder="Rechercher une configuration" />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Clé
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Valeur
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Date de création
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="configurations.length === 0">
              <td colspan="4" class="text-center text-muted py-4">
                Aucune configuration trouvée.
              </td>
            </tr>
            <tr v-for="(configuration, index) in configurations" :key="index">
              <td class="shadow-none lh-1 fw-medium text-black">
                {{ configuration.cle }}
              </td>
              <td class="shadow-none lh-1 fw-medium">
                <span :class="configuration.valeur ? 'badge bg-success' : 'badge bg-danger'">
                  {{ configuration.valeur ? 'Activé' : 'Désactivé' }}
                </span>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ format_date(configuration.createdAt) }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary pe-0">
                <button class="btn dropdown-toggle btn-primary" type="button" data-bs-toggle="dropdown"
                  aria-expanded="false">
                  Actions
                </button>
                <ul class="dropdown-menu dropdown-block">
                  <li class="dropdown-item d-flex align-items-center">
                    <a href="javascript:void(0);" @click="modifier(configuration)">
                      <i class="fa fa-pencil lh-2 me-8 position-relative top-1"></i>
                      Modifier
                    </a>
                  </li>
                  <li class="dropdown-item d-flex align-items-center">
                    <a href="javascript:void(0);"
                      @click="suppression(configuration.id, configurations, 'configuration', `la configuration ${configuration.cle}`)">
                      <i class="fa fa-trash-o lh-2 me-8 position-relative top-1"></i>
                      Supprimer
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
        <PaginationComponent :page="page" :totalPages="totalPages" :totalElements="totalElements" :limit="limit"
          @paginate="handlePaginate" />
      </div>
    </div>
  </div>

  <AddConfigurationModal :id="idConfiguration" @openmodal="showModalEdite" @close="recharger" />
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import AddConfigurationModal from './AddConfigurationModal.vue';
import ApiService from '@/services/ApiService';
import { format_date, showModal, suppression, error } from '@/utils/utils';
import { Configuration } from '@/models/Configuration';
import PaginationComponent from '@/components/Utilities/Pagination.vue';
import JwtService from '@/services/JwtService';

export default defineComponent({
  name: 'ListeConfiguration',
  components: {
    AddConfigurationModal,
    PaginationComponent,
  },
  setup() {
    const configurations = ref<Array<Configuration>>([]);
    const idConfiguration = ref(0);
    const loading = ref(false);

    // Pagination
    const searchTerm = ref('');
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(10);
    const totalElements = ref(0);

    onMounted(() => {
      getAllConfigurations();
    });

    function getAllConfigurations(p = 1, lim = 10, search = '') {
      loading.value = true;
      return ApiService.get(`/all/configuration?page=${p}&limit=${lim}&mot=${search}`)
        .then(({ data }) => {
          // Le backend renvoie soit un tableau direct, soit un objet paginé
          if (Array.isArray(data.data)) {
            configurations.value = data.data;
            totalElements.value = data.data.length;
            totalPages.value = 1;
          } else {
            configurations.value = data.data.data ?? data.data;
            totalPages.value = data.data.totalPages ?? 1;
            limit.value = data.data.limit ?? lim;
            totalElements.value = data.data.totalElements ?? configurations.value.length;
          }
        })
        .catch(({ response }) => {
          error(response?.data?.message ?? 'Erreur lors du chargement des configurations');
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const handlePaginate = ({ page_, limit_ }: { page_: number; limit_: number }) => {
      page.value = page_;
      getAllConfigurations(page_, limit_, searchTerm.value);
    };

    function rechercher() {
      getAllConfigurations(page.value, limit.value, searchTerm.value);
    }

    const recharger = () => {
      getAllConfigurations(page.value, limit.value, searchTerm.value);
    };

    function modifier(config: Configuration) {
      idConfiguration.value = config.id;
    }

    function showModalEdite(modal: any) {
      showModal(modal);
      idConfiguration.value = 0;
    }

    function resetModal() {
      idConfiguration.value = 0;
    }

    const privileges = ref<Array<string>>(JwtService.getPrivilege());
    const checkPrivilege = (name: string) => privileges.value.includes(name);

    return {
      configurations,
      idConfiguration,
      loading,
      searchTerm,
      page,
      totalPages,
      limit,
      totalElements,
      format_date,
      suppression,
      getAllConfigurations,
      handlePaginate,
      rechercher,
      recharger,
      modifier,
      showModalEdite,
      resetModal,
      checkPrivilege,
    };
  },
});
</script>
